import React, { FC, ReactElement, useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { useSelector } from 'react-redux';
import Icon from '../../Icon';
import SponsorsContacts from './SponsorsContacts/SponsorsContacts';
import SponsorQuestionForm from './SponsorPageForm/SponsorPageForm';
import { getIsLoggedIn, getIsRegistrationRequired, getSponsorPages, getContentDataIntro } from '../../../stores/reducers/userData';
import ReactHtmlParser from 'react-html-parser';
import './sponsor-page.sass';
import routes from '../../../config/routes';
import localeFieldResolver from '../../../helpers/locale-field-finder';
import { internalizationState } from '../../../stores/reducers/sections';

interface Props {
  sponsorName: string;
}
export interface SponsorContacts {
  name: string;
  name_en?: string;
  post: string;
  post_en?: string;
  email: string;
  phone: string;
}
export interface SponsorDocuments {
  title: string;
  title_en?: string;
  file: string;
}
export interface Sponsor {
  contacts: Array<SponsorContacts>;
  description: string;
  description_en?: string;
  documents: Array<SponsorDocuments>;
  email: string;
  id: string;
  logo: string;
  phone: string;
  showOfflineRequest: boolean;
  title: string;
  title_en?: string;
  type: string;
  video: string;
  webinar: string;
  website: string;
}

const SponsorPage: FC<Props> = ({ sponsorName }): ReactElement => {
  const history = useHistory();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isRegistrationRequired = useSelector(getIsRegistrationRequired);
  const shouldRegister = !isLoggedIn && isRegistrationRequired;
  if (shouldRegister) history.push(routes.REGISTER);
  const questionAvailable = isLoggedIn;

  const sponsors = useSelector(getSponsorPages);
  const { backendURL } = useSelector(getContentDataIntro);
  const [ sponsor, setSponsor ] = useState<any>({});

  const { activeLangState } = useSelector(internalizationState);
  const localizeData = {
    webinarBtnTitle_ru: 'Пообщаться с экспонентом',
    webinarBtnTitle_en: 'Chat with the exhibitor',
    webinarBtnDesc_ru: 'Для живого общения нажмите на кнопку',
    webinarBtnDesc_en: 'For live communication, click on the button',
    offlineQuestion_ru: 'Задать вопрос оффлайн',
    offlineQuestion_en: 'Ask a question offline',
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);
  
  useEffect(() => {
    setSponsor(sponsors.find((sponsor: Sponsor): boolean => {
      return sponsorName.toLowerCase() === sponsor.title.toLowerCase();
    }));
  }, [sponsorName, sponsors]);

  return (
    <>
      <div className="static-page-section">
        <div className="inner-section sponsor-section">
          <div className="container-wide">
            <div className="sponsor__title flex items-center">
              <Link
                className="btn-back transparent-btn flex mr-24"
                to={routes.EXHIBITION_OF_SPONSORS}
              >
                <svg width="22" height="35" viewBox="0 0 22 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 30.8984L17.8228 35L0 17.5L17.8228 0L22 4.10156L8.35443 17.5L22 30.8984Z" fill="#0068B5"/>
                </svg>
              </Link>
              <h1 className="sponsor__title__name">
                { localeFieldResolver(sponsor, 'title', activeLangState) }
              </h1>
            </div>

            <div className="sponsor-content flex flex-row nowrap">
              <div className="content__left col">
                <div className="sponsor__logo">
                  <img src={`${backendURL}${sponsor.logo}`} alt={localeFieldResolver(sponsor, 'title', activeLangState)}/>
                </div>
                <div className="sponsor__description mb-60">
                  {
                    ReactHtmlParser(localeFieldResolver(sponsor, 'description', activeLangState))
                  }
                </div>

                {
                  sponsor.webinar &&
                    <div className="sponsor__room flex justify-start items-center mb-32">
                      <a
                        href={sponsor.webinar}
                        className="btn btn-primary btn--mobile-min bg-footer mr-40 flex flex-row items-center"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="chat" />
                        <span className="ml-12">{ localizedName('webinarBtnTitle_ru') }</span>
                      </a>
                      <p className="col">{ localizedName('webinarBtnDesc_ru') }</p>
                    </div>
                }
                

                <div className="sponsor__action__links flex flex-column flex-wrap">
                  <ScrollLink
                    to="sponsor-form"
                    spy
                    smooth
                    duration={500}
                    className="flex items-center mb-8 sponsor-small-link"
                  >
                    <Icon name="round-question" />
                    <span className="ml-8">{ localizedName('offlineQuestion_ru') }</span>
                  </ScrollLink>
                  
                  { sponsor && sponsor.documents ?
                      sponsor.documents.map((docs: SponsorDocuments, index: number) : ReactElement => {
                        return (
                          <a
                            className="flex items-center sponsor-small-link"
                            href={`${backendURL}${docs.file}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            key={index}
                          >
                            <Icon name="presentation" />
                            <span className="ml-8">{ localeFieldResolver(docs, 'title', activeLangState) }</span>
                          </a>
                        )
                      })
                    :
                     ''
                  }
                </div>

                {
                  sponsor.video &&
                  <div className="sponsor__video flex position-relative">
                    {/* <div id="comdi-player" data-comdi-event={sponsor.video} lang="ru" ref={videoRef} /> */}
                    <iframe width="560" height="315" src={`https://www.youtube.com/embed/${sponsor.video}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" title="Видео спонсора"></iframe>
                  </div>
                }
              </div>

              <div className="content__right col">
                <SponsorsContacts sponsorData={sponsor} />
              </div>
            </div>

            {questionAvailable && (
              <SponsorQuestionForm recepient={localeFieldResolver(sponsor, 'title', activeLangState)} />
            )}
          </div>
        </div>
      </div>
    </>
  )    
}

export default SponsorPage;
